import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { StepperDialogComponent } from '@serviceplan/ui-kit';
import {
  AssetUIModel,
  CanPipe,
  ClientEnvironment,
  ClientNotificationHandlerService,
  ENVIRONMENT,
  FieldType,
  FormControlModel,
  PROMPTS,
  StepperGroupModel,
  StepperModel,
  WidthClass,
  anyAsset,
} from '@thehubz/client/core';
import { AssetDetails, Collection, CreateSharingAssetDto, CreateSharingCollectionDto, SharingApiService } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { ErrorWithMessage } from '@thehubz/shared/model';
import { lastValueFrom } from 'rxjs';
import { SelectFileOptions } from '../models/download-purpose-data';
import { TransformObjectService } from './transform-object.service';

@Injectable({
  providedIn: 'root',
})
export class SharingStepperDialogService {
  readonly CustomClients = CustomClients;
  readonly brandingKey!: CustomClients;
  readonly AnyAsset = anyAsset;
  constructor(
    @Inject(ENVIRONMENT) private env: ClientEnvironment,
    private canPipe: CanPipe,
    private transformObjectService: TransformObjectService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
    private dialog: MatDialog,
    private assetSharingService: SharingApiService,
  ) {
    this.brandingKey = env.brandingKey;
  }

  async createStepperModel(assetAspectRatio: AssetDetails[], stepLabel: string, collection = false): Promise<StepperModel> {
    const transformObject = this.transformObjectService.transformAssetDetails(assetAspectRatio);
    const stepSelectFiles: StepperGroupModel = {
      stepperId: '1',
      stepperName: $localize`Select the files`,
      stepperGroupName: 'selectFiles',
      selected: true,
      visited: true,
      formFields: [
        {
          formTypeId: '1',
          formFieldName: 'assetFileFormatList',
          fieldType: 'treeHierarchy',
          fieldLabel: $localize`File format`,
          validators: {
            required: true,
          },
          widthClass: WidthClass.WFull,
          visible: true,
          value: transformObject,
        },
      ],
      actions: [
        {
          actionLabel: PROMPTS.LABELS.NEXT,
          actionType: 'next',
          goToStep: 2,
          actionTypePosition: 'right',
          icon: 'chevron_right',
          alignIcon: 'right',
          disable: false,
          visible: false,
        },
      ],
    };

    const stepShareInfo: StepperGroupModel = {
      stepperId: '2',
      stepperName: $localize`Sharing information`,
      stepperGroupName: 'shareInfo',
      selected: false,
      visited: false,
      disable: true,
      formFields: [
        {
          formTypeId: '2',
          formFieldName: 'email',
          fieldType: 'email',
          fieldLabel: $localize`E-mail`,
          placeholder: $localize`Please enter email`,
          inputType: 'email',
          validators: {
            required: this.env.publicSharing?.mode === 'always' && this.env.publicSharing.emailMandatory !== true ? false : true,
          },
          widthClass: WidthClass.WFull,
          visible: true,
          value: '',
        },
        {
          formTypeId: '4',
          formFieldName: 'description',
          fieldType: 'textarea',
          fieldLabel: $localize`Description`,
          placeholder: $localize`Description`,
          inputType: 'text',
          validators: {
            required: true,
          },
          widthClass: WidthClass.WFull,
          visible: true,
          value: '',
        },
        {
          formTypeId: '5',
          formFieldName: 'expiryDate',
          fieldType: 'date',
          fieldLabel: $localize`Expiry date`,
          inputType: 'date',
          widthClass: `${WidthClass.W13} min-w-[20ch]`,
          placeholder: $localize`DD/MM/YYYY`,
          functionCall: false,
          validators: {
            required: true,
          },
          value: new Date(new Date().setDate(new Date().getDate() + 7)),
          minDate: new Date(new Date().setDate(new Date().getDate() + 1)),
          maxDate: new Date(new Date().setDate(new Date().getDate() + 30)),
          visible: true,
        },
      ],
      actions: [
        {
          actionLabel: PROMPTS.LABELS.BACK,
          actionType: 'back',
          goToStep: 1,
          actionTypePosition: 'left',
          icon: 'chevron_left',
          alignIcon: 'left',
          disable: false,
          visible: false,
        },
        {
          actionLabel: $localize`Share`,
          actionTypePosition: 'right',
          icon: 'share',
          alignIcon: 'left',
          actionType: 'shareAsset',
          disable: false,
          visible: false,
        },
      ],
      altActions: [
        {
          actionLabel: PROMPTS.LABELS.BACK,
          actionType: 'back',
          goToStep: 1,
          actionTypePosition: 'left',
          icon: 'chevron_left',
          alignIcon: 'left',
          disable: false,
          visible: false,
        },
        {
          actionLabel: PROMPTS.LABELS.NEXT,
          actionType: 'next',
          goToStep: 3,
          actionTypePosition: 'right',
          icon: 'chevron_right',
          alignIcon: 'right',
          disable: false,
          visible: false,
        },
      ],
    };
    const assetAccess = this.canPipe.transform(this.AnyAsset, 'downloadonlylowres');
    if (this.brandingKey === CustomClients.Vorwerk && !assetAccess) {
      const selectedOptionsField: FormControlModel = {
        formTypeId: '6',
        formFieldName: 'selectedOptions',
        fieldType: FieldType.MultiSelectChip,
        fieldLabel: $localize`Select files`,
        placeholder: PROMPTS.PLACEHOLDER.DROPDOWN_SELECT_MULTIPLE,
        inputType: '',
        mandatory: false,
        widthClass: WidthClass.W13,
        sortDataSource: false,
        disable: false,
        visible: true,
        options: SelectFileOptions,
      };

      stepSelectFiles.formFields?.unshift(selectedOptionsField);
    }
    if (this.brandingKey === CustomClients.VKB) {
      const sharingWithoutDownloadCheckbox: FormControlModel = {
        formTypeId: '6',
        formFieldName: 'noDownload',
        fieldType: 'checkbox',
        fieldLabel: $localize`Asset sharing without download (lightdesk)`,
        widthClass: WidthClass.WFull,
        value: '',
        validators: {
          required: false,
        },
        visible: true,
      };
      stepShareInfo.formFields?.push(sharingWithoutDownloadCheckbox);
    }

    const stepper = [stepSelectFiles, stepShareInfo];

    if (this.env.publicSharing) {
      // add checkbox for public link
      stepShareInfo.formFields?.unshift({
        formTypeId: '1',
        formFieldName: 'publicLink',
        fieldType: 'checkbox',
        fieldLabel: $localize`Generate public link`,
        widthClass: WidthClass.WFull,
        visible: this.env.publicSharing.mode === 'select',
        value: this.env.publicSharing.mode === 'always',
        validators: {
          required: false,
        },
      });

      if (this.env.publicSharing.mode === 'always') {
        stepShareInfo.actions = stepShareInfo.altActions;
        delete stepShareInfo.altActions;
      }

      // add public link step
      const sharingId = await lastValueFrom(this.assetSharingService.sharingControllerGenerateId());
      stepper.push({
        stepperId: '3',
        stepperName: $localize`Link to shared assets`,
        stepperGroupName: 'publicLink',
        selected: false,
        visited: false,
        disable: true,
        hidden: this.env.publicSharing.mode === 'select' ? true : false,
        formFields: [
          {
            formTypeId: '0',
            formFieldName: 'sharingId',
            fieldType: 'text',
            fieldLabel: '',
            widthClass: WidthClass.SelfCenter,
            visible: false,
            value: sharingId.id,
          },
          {
            formTypeId: '1',
            formFieldName: 'publicLink',
            fieldType: 'copyToClipboard',
            fieldLabel: '',
            widthClass: WidthClass.WFull,
            visible: true,
            value: this.createPublicSharingLink(sharingId.id, collection),
          },
        ],
        actions: [
          {
            actionLabel: PROMPTS.LABELS.BACK,
            actionType: 'back',
            goToStep: 2,
            actionTypePosition: 'left',
            icon: 'chevron_left',
            alignIcon: 'left',
            disable: false,
            visible: false,
          },
          {
            actionLabel: $localize`Share`,
            actionTypePosition: 'right',
            icon: 'share',
            alignIcon: 'left',
            actionType: 'shareAsset',
            disable: false,
            visible: false,
          },
        ],
      });
    }

    return {
      stepID: 'shareModel',
      stepLabel: stepLabel,
      stepper,
    };
  }

  openSharingDialog(stepperData: StepperModel) {
    this.dialog
      .open(StepperDialogComponent, {
        width: '90%',
        height: '90%',
        maxWidth: '100vw',
        disableClose: true,
        panelClass: 'custom-dialog-container',
        data: { stepperData },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          const assetDetails: CreateSharingAssetDto = {
            _id: result.data.publicLink?.sharingId,
            expiryDate: result.data.shareInfo.expiryDate,
            userEmails: result.data.shareInfo.email,
            publicLink: result.data.shareInfo.publicLink,
            noDownload: result.data.shareInfo.noDownload,
            description: result.data.shareInfo.description,
            assetDetails: result.data.selectFiles.assetFileFormatList,
          };
          this.assetSharingService.sharingControllerShareAsset(assetDetails).subscribe({
            next: () => {
              if (assetDetails.userEmails && assetDetails.userEmails.length > 0) {
                this.clientNotificationHandlerService.successNotification($localize`Email sent successfully`);
              }
            },
            error: (err: ErrorWithMessage) => {
              this.clientNotificationHandlerService.errorNotification(err);
            },
          });
        }
      });
  }

  private createPublicSharingLink(sharingId: string, collection: boolean) {
    let link = `${this.env.publicSharing?.baseUrl}/shared/assets/${sharingId}`;
    if (collection) {
      link += ';viewType=collection';
    }
    return link;
  }

  /**
   * open stepper dialog for sharing collection
   */
  async collectionSharingStepper(assetAspectRatio: AssetDetails[], collectionInfo: Collection, collectionId: string, stepLabel: string) {
    const stepperData = await this.createStepperModel(assetAspectRatio, stepLabel, true);
    this.dialog
      .open(StepperDialogComponent, {
        width: '90%',
        height: '90%',
        maxWidth: '100vw',
        disableClose: true,
        panelClass: 'custom-dialog-container',
        data: { stepperData: stepperData },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result && stepperData.stepID === 'shareModel') {
          const coverAsset = collectionInfo.coverAssetID;
          const assetDetails: CreateSharingCollectionDto = {
            _id: result.data.publicLink?.sharingId,
            expiryDate: result.data.shareInfo.expiryDate,
            userEmails: result.data.shareInfo.email,
            publicLink: result.data.shareInfo.publicLink,
            description: result.data.shareInfo.description,
            assetDetails: result.data.selectFiles.assetFileFormatList,
            ownerID: collectionInfo.ownerIDs?.[0] || '',
            coverAssetID: coverAsset && typeof coverAsset === 'object' ? (coverAsset as AssetUIModel)._id : coverAsset || '',
            collectionID: collectionId,
          };
          this.assetSharingService.sharingControllerShareCollection(assetDetails).subscribe({
            next: () => {
              if (assetDetails.userEmails && assetDetails.userEmails.length > 0) {
                this.clientNotificationHandlerService.successNotification($localize`Email sent successfully`);
              }
            },
            error: (err: ErrorWithMessage) => {
              this.clientNotificationHandlerService.errorNotification(err);
            },
          });
        }
      });
  }
}
